import { useState, useEffect, useMemo } from "react";
import { useProjectStore } from "../../store/useProjectStore";
import { useClientStore } from "../../store/useClientStore";
import { useAuthStore } from "../../store/useAuthStore";
import { useFinanceStore } from "../../store/useFinanceStore";
import { useQuestionnaireStore } from "../../store/useQuestionnaireStore";
import toast from "react-hot-toast";
import { X, Plus, Trash2 } from "lucide-react";
import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";
import { mergeCategoryNames, subcategoriesFor } from "../../config/categoryConfig";
import Button from "../ui/Button";
import MilestonesEditor from "./MilestonesEditor";
import type { Milestone } from "../../store/useProjectStore";

interface EditProjectModalProps {
  project: any;
  onClose: () => void;
}

const EditProjectModal = ({ project, onClose }: EditProjectModalProps) => {
  const { updateProject, isUpdatingProject } = useProjectStore();
  const { clients, fetchClients } = useClientStore();
  const { authUser } = useAuthStore();
  const { accounts, fetchSummary } = useFinanceStore();
  const { questionnaires, getAllQuestionnaires } = useQuestionnaireStore();


  const categoryOptions = [
    { value: "", label: "Select Category" },
    ...mergeCategoryNames([]).map((n) => ({ value: n, label: n })),
  ];

  const projectStatusOptions = [
    { value: "Not Started", label: "Not Started" },
    { value: "In Progress", label: "In Progress" },
    { value: "Completed", label: "Completed" },
    { value: "Cancelled", label: "Cancelled" },
  ];

  const paymentStatusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Partially Released", label: "Partially Released" },
    { value: "Paid", label: "Paid" },
  ];

  const clientOptions = [
    { value: "", label: "Select Contact" },
    ...clients.map((client: any) => ({ value: client._id, label: client.username ? `${client.name} · @${client.username}` : client.name })),
  ];

  const formatDate = (date?: string | Date) =>
    date ? new Date(date).toISOString().split("T")[0] : "";

  const [formData, setFormData] = useState({
    name: "",
    assignedContact: "",
    description: "",
    projectType: "",
    projectSubType: "",
    designType: "",
    supervisor: "",
    projectStatus: "Not Started",
    eta: "",
    source: "Upwork",
    billingType: "Hourly",
    hourlyRate: "",
    estimatedHours: "",
    projectPrice: "",
    upfrontPayment: "",
    paymentMethod: "",
    escrowAmount: "",
    releasedPayment: "",
    paymentStatus: "Pending",
    upworkAccount: "",
    linkedQuestionnaire: "",
    resourceCosts: [] as Array<{
      label: string;
      type: "fixed" | "hourly" | "percentage";
      amount: number;
      hours?: number;
    }>,
    milestones: [] as Milestone[],
  });

  useEffect(() => {
    if (!accounts.length) fetchSummary();
    getAllQuestionnaires();
  }, []);

  // Questionnaires belonging to the selected contact (+ keep the current link visible).
  const contactQuestionnaires = useMemo(
    () => questionnaires.filter((q: any) =>
      (q.clientId?._id || q.clientId) === formData.assignedContact ||
      q._id === (project?.linkedQuestionnaire?._id || project?.linkedQuestionnaire)
    ),
    [questionnaires, formData.assignedContact, project]
  );

  useEffect(() => {
    fetchClients();
    if (project) {
      setFormData({
        name: project.name || "",
        assignedContact: project.assignedContact?._id || "",
        description: project.description || "",
        projectType: project.projectType || "",
        projectSubType: project.projectSubType || "",
        designType: project.designType || "",
        supervisor: authUser?.fullName || "",
        projectStatus: project.projectStatus || "Not Started",
        eta: formatDate(project.eta) || "",
        source: project.source || "Upwork",
        billingType: project.billingType || "Hourly",
        hourlyRate: project.hourlyRate?.toString() || "",
        estimatedHours: project.estimatedHours?.toString() || "",
        projectPrice: project.projectPrice?.toString() || "",
        upfrontPayment: project.upfrontPayment?.toString() || "",
        paymentMethod: project.paymentMethod || "",
        escrowAmount: project.escrowAmount?.toString() || "",
        releasedPayment: project.releasedPayment?.toString() || "",
        paymentStatus: project.paymentStatus || "Pending",
        upworkAccount:
          typeof project.upworkAccount === "object"
            ? project.upworkAccount?._id || ""
            : project.upworkAccount || "",
        linkedQuestionnaire: project.linkedQuestionnaire?._id || project.linkedQuestionnaire || "",
        resourceCosts: project.resourceCosts || [],
        milestones: project.milestones || [],
      });
    }
  }, [project, authUser]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addResourceCost = () => {
    setFormData((prev) => ({
      ...prev,
      resourceCosts: [
        ...prev.resourceCosts,
        { label: "", type: "fixed", amount: 0, hours: 0 },
      ],
    }));
  };

  const updateResourceCost = (
    i: number,
    patch: Partial<{ label: string; type: "fixed" | "hourly" | "percentage"; amount: number; hours: number }>
  ) => {
    setFormData((prev) => ({
      ...prev,
      resourceCosts: prev.resourceCosts.map((c, idx) =>
        idx === i ? { ...c, ...patch } : c
      ),
    }));
  };

  const removeResourceCost = (i: number) => {
    setFormData((prev) => ({
      ...prev,
      resourceCosts: prev.resourceCosts.filter((_, idx) => idx !== i),
    }));
  };

  const revenue =
    (Number(formData.escrowAmount) || 0) + (Number(formData.releasedPayment) || 0);
  const totalResourceCost = formData.resourceCosts.reduce((sum, c) => {
    if (c.type === "fixed") return sum + (c.amount || 0);
    if (c.type === "hourly") return sum + (c.amount || 0) * (c.hours || 0);
    if (c.type === "percentage") return sum + (revenue * (c.amount || 0)) / 100;
    return sum;
  }, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dataToSend: any = {
        ...formData,
        escrowAmount: Number(formData.escrowAmount) || 0,
        releasedPayment: Number(formData.releasedPayment) || 0,
        hourlyRate: Number(formData.hourlyRate) || 0,
        estimatedHours: Number(formData.estimatedHours) || 0,
        projectPrice: Number(formData.projectPrice) || 0,
        upfrontPayment: Number(formData.upfrontPayment) || 0,
        designType: formData.designType,
        resourceCosts: formData.resourceCosts,
        milestones: formData.milestones,
        upworkAccount: formData.upworkAccount || null,
      };

      await updateProject(project._id, dataToSend);
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update project");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <div className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl border border-white/50 flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 fade-in-50 duration-300">
        <div className="flex items-center justify-between px-6 sm:px-8 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#2462b2] to-[#1e55a0]">
            Edit Project
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 transition group"
          >
            <X size={22} className="text-gray-500 group-hover:text-gray-900 transition" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto cre-scroll px-6 sm:px-8 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InputField
              label="Project Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <SelectField
              label="Assigned Contact"
              name="assignedContact"
              value={formData.assignedContact}
              onChange={handleChange}
              options={clientOptions}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Project Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Describe the project scope, the client's requirements and any shared conversation…"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2462b2]/30 bg-white"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <SelectField
              label="Category"
              name="projectType"
              value={formData.projectType}
              onChange={handleChange}
              options={categoryOptions}
              required
            />
            <SelectField
              label="Subcategory"
              name="projectSubType"
              value={formData.projectSubType}
              onChange={handleChange}
              options={[
                { value: "", label: "Select Subcategory" },
                ...subcategoriesFor(formData.projectType).map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <SelectField
              label="Source"
              name="source"
              value={formData.source}
              onChange={handleChange}
              options={[
                { value: "Upwork", label: "Upwork" },
                { value: "Direct", label: "Direct Project" },
                { value: "Invitation", label: "Invitation (no connects)" },
              ]}
            />
          </div>

          {/* Billing & value */}
          <div className="rounded-xl border border-gray-200 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-700">Billing & Value</h3>
              <SelectField
                label=""
                name="billingType"
                value={formData.billingType}
                onChange={handleChange}
                options={[{ value: "Hourly", label: "Hourly" }, { value: "Fixed", label: "Fixed Price" }]}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InputField label="Hourly Rate ($/hr)" name="hourlyRate" type="number" value={formData.hourlyRate} onChange={handleChange} />
              <InputField label="Estimated Hours" name="estimatedHours" type="number" value={formData.estimatedHours} onChange={handleChange} />
              <InputField label="Total Project Value ($)" name="projectPrice" type="number" value={formData.projectPrice} onChange={handleChange} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Upfront Payment ($)" name="upfrontPayment" type="number" value={formData.upfrontPayment} onChange={handleChange} />
              <SelectField
                label="Payment Method"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
                options={[
                  { value: "", label: "Select Method" },
                  { value: "PayPal", label: "PayPal" }, { value: "Payoneer", label: "Payoneer" },
                  { value: "Bank Transfer", label: "Bank Transfer" }, { value: "Stripe", label: "Stripe" },
                  { value: "Wise", label: "Wise" }, { value: "Cash", label: "Cash" }, { value: "Other", label: "Other" },
                ]}
              />
            </div>
          </div>

          {/* Linked questionnaire */}
          <SelectField
            label="Linked Questionnaire"
            name="linkedQuestionnaire"
            value={formData.linkedQuestionnaire}
            onChange={handleChange}
            options={[
              { value: "", label: "— None —" },
              ...contactQuestionnaires.map((q: any) => {
                const who = [q.clientId?.name, (q.contactUsername || q.clientId?.username) && `@${q.contactUsername || q.clientId?.username}`].filter(Boolean).join(" · ");
                return { value: q._id, label: who ? `${q.name} — ${who}` : q.name };
              }),
            ]}
          />

          <InputField label="Supervisor" name="supervisor" value={formData.supervisor} disabled />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SelectField
              label="Status"
              name="projectStatus"
              value={formData.projectStatus}
              onChange={handleChange}
              options={projectStatusOptions}
            />
            <InputField
              label="ETA (Expected Delivery)"
              type="date"
              name="eta"
              value={formData.eta}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InputField
              label="Escrow Amount"
              name="escrowAmount"
              type="number"
              value={formData.escrowAmount}
              onChange={handleChange}
            />
            <InputField
              label="Released Payment"
              name="releasedPayment"
              type="number"
              value={formData.releasedPayment}
              onChange={handleChange}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <SelectField
              label="Payment Status"
              name="paymentStatus"
              value={formData.paymentStatus}
              onChange={handleChange}
              options={paymentStatusOptions}
            />
            <SelectField
              label="Upwork Account"
              name="upworkAccount"
              value={formData.upworkAccount}
              onChange={handleChange}
              options={[
                { value: "", label: "Not Assigned" },
                ...accounts.map((a: { accountId: string; accountName: string }) => ({
                  value: a.accountId,
                  label: a.accountName,
                })),
              ]}
            />
          </div>

          <div className="pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                  Resource Costs
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Costs incurred to deliver this project (deducted from net profit)
                </p>
              </div>
              <button
                type="button"
                onClick={addResourceCost}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white hover:opacity-95 transition"
              >
                <Plus size={14} /> Add Cost
              </button>
            </div>

            {formData.resourceCosts.length === 0 ? (
              <div className="p-4 rounded-xl border-2 border-dashed border-gray-200 text-center text-xs text-gray-400 italic">
                No resource costs added yet. Click "Add Cost" above.
              </div>
            ) : (
              <div className="space-y-2">
                {formData.resourceCosts.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 bg-gray-50/50"
                  >
                    <input
                      placeholder="Label (e.g. Designer)"
                      className="flex-1 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#2462b2]/30"
                      value={c.label}
                      onChange={(e) => updateResourceCost(i, { label: e.target.value })}
                    />
                    <select
                      className="px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#2462b2]/30"
                      value={c.type}
                      onChange={(e) =>
                        updateResourceCost(i, { type: e.target.value as any })
                      }
                    >
                      <option value="fixed">Fixed $</option>
                      <option value="hourly">Hourly</option>
                      <option value="percentage">% of Revenue</option>
                    </select>
                    <input
                      type="number"
                      placeholder={c.type === "percentage" ? "%" : "$"}
                      className="w-20 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#2462b2]/30"
                      value={c.amount}
                      onChange={(e) =>
                        updateResourceCost(i, { amount: Number(e.target.value) || 0 })
                      }
                    />
                    {c.type === "hourly" && (
                      <input
                        type="number"
                        placeholder="hrs"
                        className="w-16 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#2462b2]/30"
                        value={c.hours || 0}
                        onChange={(e) =>
                          updateResourceCost(i, { hours: Number(e.target.value) || 0 })
                        }
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeResourceCost(i)}
                      className="p-1.5 rounded-md border border-gray-200 text-red-500 hover:bg-red-50 transition"
                      title="Remove"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}

                <div className="flex items-center justify-between mt-3 px-3 py-2 rounded-xl bg-blue-50 border border-blue-200">
                  <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                    Total Resource Cost
                  </span>
                  <span className="text-sm font-extrabold text-blue-900">
                    ${totalResourceCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>

          <MilestonesEditor
            milestones={formData.milestones}
            onChange={(milestones) =>
              setFormData((prev) => ({ ...prev, milestones }))
            }
          />
          </div>

          <div className="shrink-0 flex justify-end gap-3 px-6 sm:px-8 py-4 border-t border-gray-100 bg-gray-50/60">
            <Button
              type="button"
              onClick={onClose}
              className="bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold px-6 py-2.5 rounded-xl transition-all"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isUpdatingProject}
              className="bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white font-bold px-8 py-2.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {isUpdatingProject ? "Updating..." : "Update Project"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProjectModal;
